// =============================================================================
// resourceSearch.ts — pure client-side search over Resources pages
// =============================================================================
// The Resources wiki is a small, supervisor-authored corpus, so search is a
// client-side keyword/substring filter — no Postgres full-text (docs/
// resources-search-plan.md). Swap searchResources() for an FTS RPC behind the
// same signature if the corpus ever grows large. No I/O; fully unit-tested.
// =============================================================================

export interface SearchablePage {
  id: string;
  section: string;
  title: string;
  body: string | null;
  parent_id?: string | null;
  sort_order?: number;
}

export interface ResourceSearchResult<T> {
  page: T;
  snippet: string; // plain-text body window around the first match, or ""
  inTitle: boolean; // matched in the title (ranks above body-only matches)
}

// Strip HTML tags to plain text: drop tags, decode a few common entities,
// collapse whitespace. Good enough for matching + snippets (not for display).
export function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

// Split a query into lowercase terms (whitespace-separated, empties dropped).
function queryTerms(query: string): string[] {
  return (query || "").toLowerCase().split(/\s+/).filter(Boolean);
}

// A short plain-text snippet of `text` centred on the first occurrence of `term`.
function snippetAround(text: string, term: string, radius = 60): string {
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx < 0) return "";
  const start = Math.max(0, idx - radius);
  const end = Math.min(text.length, idx + term.length + radius);
  return (start > 0 ? "… " : "") + text.slice(start, end).trim() + (end < text.length ? " …" : "");
}

// Rank pages against a keyword query. A page matches when EVERY term appears in
// its title or (stripped) body, case-insensitive. Title matches sort first, then
// title A→Z. Empty/blank query → [].
export function searchResources<T extends SearchablePage>(
  pages: T[],
  query: string
): ResourceSearchResult<T>[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return [];
  const results: ResourceSearchResult<T>[] = [];
  for (const page of pages || []) {
    const title = (page.title || "").toLowerCase();
    const bodyText = stripHtml(page.body);
    const bodyLower = bodyText.toLowerCase();
    const haystack = `${title} ${bodyLower}`;
    if (!terms.every((t) => haystack.includes(t))) continue;
    const inTitle = terms.some((t) => title.includes(t));
    let snippet = "";
    for (const t of terms) {
      if (bodyLower.includes(t)) {
        snippet = snippetAround(bodyText, t);
        break;
      }
    }
    results.push({ page, snippet, inTitle });
  }
  results.sort((a, b) => {
    if (a.inTitle !== b.inTitle) return a.inTitle ? -1 : 1;
    return (a.page.title || "").localeCompare(b.page.title || "");
  });
  return results;
}
